import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, useTheme } from '@mui/material';
import { defaultColumns } from './helpers/defaults';
import useStateStorage from './helpers/useStateStorage';
import useGridDataSource from './helpers/useGridDataSource';
import {
  DataGrid,
  DataGridRef,
  Column,
  Selection,
  ColumnFixing,
  GroupPanel,
  StateStoring,
  ColumnChooser,
  FilterRow,
  SearchPanel,
  Scrolling,
  Paging,
  LoadPanel,
  Pager,
  Grouping,
  MasterDetail,
  Toolbar,
  Sorting,
  FilterPanel,
  HeaderFilter,
  FilterBuilder
} from 'devextreme-react/data-grid';
import { NewDataGridProps } from './datagrid-types';
import useToolbarItems from './helpers/useToolbarItems';
import {
  determinScrollMode,
  determineRemoteOperations,
  handleCellDoubleClick,
  handleCellSingleClick,
  handleDeselectedRowStyle,
  handleRowDoubleClick,
  handleRowSingleClick,
  handleSelectAllCheckboxStyle,
  handleSelectedRowStyle
} from './helpers/helpers';
import parseAnyPredifinedCustomColumns from './helpers/parseAnyPredefinedCustomColumns';
import { PositionConfig } from 'devextreme/animation/position';
import { useAppDispatch } from 'src/store/baseHooks';
import { subscribe } from 'src/@core/utils/vigoCustomEvent';
import { useNewGridApiRefContext } from 'src/@core/context/newGridRefContext';

const columnChooserPorition: PositionConfig = {
  my: 'right top',
  at: 'right bottom',
  of: '.dx-datagrid-column-chooser-button'
};

/**
 * A customizable data grid component that uses the DevExtreme React Data Grid library.
 *
 * Data to be displayed in the grid can be passed as:
 * - an array to the 'datasource' property
 * - a CustomStore instance to the 'customStore' property
 * - a paging options object to the 'pagingOptions' property. This is to use an endpoint that provides a paginated response,
 *    setting 'scrollMode' as 'virtual' will give an infintie scroll experience, setting 'scrollMode' as 'standard' will give a traditional paginated experience.
 *   - When using paging options, remote operations will be enabled for paging (you can override all these properties).
 *
 * @param {NewDataGridProps} props - The props for the NewDataGrid component.
 * @returns {JSX.Element} - The NewDataGrid component.
 */
const NewDataGrid = ({
  id = 'dataGrid',
  name = '',
  datasource,
  customStore,
  columns = [],
  height = '100%',
  keyExpr,
  columnMinWidth = 20,
  columnAutoWidth = true,
  columnResizingMode = 'widget',
  allowColumnReordering = true,
  allowColumnResizing = true,
  allowRowSelection = false,
  showGroupingPanel = true,
  allowGridStateStorage = true,
  allowColumnFixing = true,
  allowExport = false,
  selectionMode = 'multiple',
  selectAllMode = 'page',
  showCheckboxesMode = 'always',
  savingTimeout = 1000,
  showColumnChooser = false,
  columnChooserMode = 'select',
  showFilterRow = false,
  showFilterPanel = false,
  showHeaderFilter = false,
  showSearchPanel = true,
  customActions = [],
  defaultActions = { editToolTip: '' },
  hideActions = false,
  rowAlternationEnabled = false,
  showBorders = false,
  showColumnLines = false,
  showRowLines = false,
  disableLayoutReset = false,
  pageSize,
  scrollMode,
  autoExpandGroups = true,
  pagerVisible = true,
  pagerDisplayMode = 'adaptive',
  pagerShowInfo = true,
  pagerInfoText = '({2} items)',
  masterDetailEnabled = false,
  masterDetailComponent,
  pagingOptions,
  remoteOperations,
  customToolbarComponents = [],
  filterColumn = '',
  filter,
  defaultGridState,
  disableSelectionOnClick = false,
  disableVirtualization = true,
  hideHeader = false,
  onScrollBottom,
  onRowSelection,
  onStateChange,
  onRowClick,
  onRowDblClick,
  onCellClick,
  onCellDblClick,
  AdvancedSearch,
  ignoreDates = false
}: NewDataGridProps) => {
  // State
  const [gridHeight, setGridHeight] = useState<string>();

  const theme = useTheme();
  const gridRef = useRef<DataGridRef | null>(null);
  const uiFlowDispatch = useAppDispatch();
  const { dispatch: newGridRefDispatch } = useNewGridApiRefContext();

  const { fetchStoredGridState, saveGridStateToStorage, resetState, storageKey } = useStateStorage({
    enabled: allowGridStateStorage,
    name,
    defaultState: defaultGridState,
    ref: gridRef,
    filterColumn,
    onStateChange
  });

  const store = useGridDataSource({
    pagingOptions: pagingOptions ?? { enable: false },
    datasource,
    customStore,
    pageSize,
    columns,
    onScrollBottom,
    ignoreDates
  });

  const items = useToolbarItems({
    resetState,
    disableLayoutReset,
    customToolbarComponents,
    storageKey,
    allowExport,
    filter: filterColumn,
    columns
  });

  /**
   * An array of Column components generated from the generatedColumns array.
   */
  const renderColumns = useMemo(() => {
    return [
      ...parseAnyPredifinedCustomColumns(columns),
      ...defaultColumns(customActions, uiFlowDispatch, defaultActions, hideActions, gridRef)
    ].map(col => <Column key={col?.caption} fixedPosition="right" {...col} />);
  }, [columns, customActions, defaultActions, hideActions, uiFlowDispatch]);

  const handleRowSelectionChange = useCallback(
    (e: any) => {
      const { selectedRowKeys, currentDeselectedRowKeys } = e;
      const grid = gridRef.current?.instance();

      handleSelectedRowStyle(grid, selectedRowKeys, theme);

      handleDeselectedRowStyle(grid, currentDeselectedRowKeys);

      handleSelectAllCheckboxStyle(grid, theme);

      onRowSelection?.(e, grid?.getDataSource?.()?.items?.() ?? []);
    },
    [onRowSelection, theme]
  );

  const handleRowUpdated = useCallback(
    ({ data, key }: { data: any; key: any }) => defaultActions && defaultActions.rowEditCallBack?.(data, key),
    [defaultActions]
  );

  const rowSingleClick = useCallback(
    (e: any) => handleRowSingleClick(e, disableSelectionOnClick, onRowClick),
    [disableSelectionOnClick, onRowClick]
  );

  const rowDoubleClick = useCallback(
    (e: any) => handleRowDoubleClick(e, disableSelectionOnClick, onRowDblClick),
    [disableSelectionOnClick, onRowDblClick]
  );

  const cellClick = useCallback((e: any) => handleCellSingleClick(e, onCellClick), [onCellClick]);

  const cellDoubleClick = useCallback((e: any) => handleCellDoubleClick(e, onCellDblClick), [onCellDblClick]);

  const handleRemoteOperations = useMemo(() => {
    return determineRemoteOperations(remoteOperations, pagingOptions ?? { enable: false });
  }, [pagingOptions, remoteOperations]);

  const scrollModeMemo = useMemo(() => {
    return determinScrollMode(scrollMode, pagingOptions ?? { enable: false });
  }, [pagingOptions, scrollMode]);

  const cellPreparedHandler = useCallback((e: any) => {
    if (e.rowType === 'data') {
      e.cellElement.style.padding = '0px 10px 0px 10px';
    }

    if (e.column.caption === 'Actions') {
      e.column.width = '90px';
      e.column.maxwidth = '90px ';
      e.cellElement.style.backgroundColor = 'rgba(236, 236, 240, 0.48)';
    }
  }, []);

  const onEditorPreparingHandler = useCallback((e: any) => {
    if (e.parentType === 'searchPanel') {
      e.editorOptions.stylingMode = 'outlined';
      e.editorOptions.showClearButton = true;
    }
  }, []);

  useEffect(() => {
    newGridRefDispatch({ type: 'setGridApiRef', payload: gridRef });
  }, [gridRef, newGridRefDispatch]);

  const isFilterMountedRef = useRef(false);

  useEffect(() => {
    // customStore reads the filter via a ref internally, so the grid needs to be told to reload when it changes
    if (!isFilterMountedRef.current) {
      isFilterMountedRef.current = true;

      return;
    }

    gridRef.current?.instance()?.refresh(true);
  }, [filter]);

  useEffect(() => {
    document.body.classList.toggle('darkmode', theme.palette.mode === 'dark');
  }, [theme.palette.mode]);

  useEffect(() => {
    const handler = (e: any) => {
      setGridHeight(e.detail.val);
    };

    const unsubscribe = subscribe('toggleAdvancedSearch', handler);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack direction={'column'} height={'100%'}>
      {AdvancedSearch && <AdvancedSearch />}
      <DataGrid
        id={id}
        width={'100%'}
        ref={gridRef}
        className={theme.palette.mode === 'dark' ? 'darkmodeDatagrid' : 'lightmodeDatagrid'}
        onCellPrepared={cellPreparedHandler}
        remoteOperations={handleRemoteOperations}
        cacheEnabled={false}
        onSelectionChanged={handleRowSelectionChange}
        onContentReady={e => {
          const grid = e.component;
          const selectedRowKeys = grid?.getSelectedRowKeys();
          if (selectedRowKeys && selectedRowKeys.length > 0) {
            handleSelectedRowStyle(grid, selectedRowKeys, theme);
            handleSelectAllCheckboxStyle(grid, theme);
          }
        }}
        onEditorPreparing={onEditorPreparingHandler}
        wordWrapEnabled={true}
        onRowUpdated={handleRowUpdated}
        onRowClick={rowSingleClick}
        onRowDblClick={rowDoubleClick}
        onCellClick={cellClick}
        onCellDblClick={cellDoubleClick}
        hoverStateEnabled={true}
        height={gridHeight ? gridHeight : height}
        columnAutoWidth={columnAutoWidth}
        columnMinWidth={columnMinWidth}
        columnResizingMode={columnResizingMode}
        dataSource={store}
        showBorders={showBorders}
        showColumnLines={showColumnLines}
        showRowLines={showRowLines}
        allowColumnReordering={allowColumnReordering}
        allowColumnResizing={allowColumnResizing}
        rowAlternationEnabled={rowAlternationEnabled}
        {...{ ...(datasource && Array.isArray(datasource) && { keyExpr }) }}>
        {hideHeader === false && <Toolbar>{items}</Toolbar>}
        <Paging defaultPageSize={pageSize} pageSize={pageSize} />
        <GroupPanel visible={showGroupingPanel && !hideHeader} />
        <Grouping autoExpandAll={autoExpandGroups} contextMenuEnabled={true} />
        <Sorting mode="single" />
        {showColumnChooser === true && (
          <ColumnChooser enabled={showColumnChooser} mode={columnChooserMode} position={columnChooserPorition} />
        )}
        {hideHeader === false && <SearchPanel visible={showSearchPanel}></SearchPanel>}

        <FilterRow visible={showFilterRow} />
        <FilterPanel visible={showFilterPanel} />
        <FilterBuilder groupOperations={['and', 'or']} />
        <HeaderFilter visible={showHeaderFilter && !hideHeader} />

        <ColumnFixing enabled={allowColumnFixing} />

        <Selection
          mode={allowRowSelection ? selectionMode : 'none'}
          selectAllMode={selectAllMode}
          showCheckBoxesMode={showCheckboxesMode}
        />
        <Scrolling
          mode={scrollModeMemo}
          preloadEnabled={true}
          showScrollbar="always"
          rowRenderingMode={disableVirtualization ? 'standard' : 'virtual'}
          renderAsync={true}
        />

        {renderColumns}

        <MasterDetail enabled={masterDetailEnabled} component={masterDetailComponent} />

        <StateStoring
          enabled={allowGridStateStorage}
          type={'custom'}
          customLoad={fetchStoredGridState}
          customSave={saveGridStateToStorage}
          savingTimeout={savingTimeout}
        />

        <Pager
          visible={pagerVisible}
          displayMode={pagerDisplayMode}
          infoText={pagerInfoText}
          showInfo={pagerShowInfo}
        />
        <LoadPanel enabled={true} />
      </DataGrid>
    </Stack>
  );
};

export default React.memo(NewDataGrid);
